import {
  applicationStore,
  gameConfigStore,
  gameResultStore,
  loadingStore,
  popupStore,
  roundInfoStore,
  userCountStore,
  userInfoStore,
  userWinCountStore,
} from '@pascal/pascal-core/shared/core/store';
import {
  freeBetStore,
  isMobile,
  parseNumberByPrecision,
  RoundStatus,
  tableBetHistoryStore,
} from '@pascal/pascal-core';

import { PopUps } from '@pascal/pascal-core/table-line/t_types/enums/PopupTypes';
import { userSettingsStore } from '@pascal/pascal-core/shared/core/store/userSettingsStore';
import { roundTimeStore } from '../store/roundTimeStore';

import { worker } from '../index';
import { liveConnection } from '../core';
import { gameLiveConnection } from './GameConnection';
import { cardsStore } from '../store/cardsStore';
import { limitsAndPayoutsStore } from '../store/limitsAndPayoutsStore';
import { CARDS } from '../enums/cardTypes';
import { roundsWinBetTypes } from '../store/roundsWinBetTypes';
import { doBetStore } from '../store/doBetStore';
import { soundService, soundTypes } from './SoundService';
import { gameStore } from '../store/gameStore';
import { winningAmountStore } from '../store/winningAmountStore';
import { MobileShuffleAnimation, WebShuffleAnimation } from '../core/constants/events';
import { SHUFFLED_MESSAGE } from '../enums/constants';
import { betResponseCodeByName } from '../enums/betResponseCode';
import { BET_TYPES, BET_TYPES_ODDS } from '../enums/cardOwner';
// import { cashbackStore } from '../store/cashbackStore';

let timeOutId;

const animationsTimeOutId = [];
let shuffleAnimationTimeOutId;
let endShuffleAnimationTimeOutId;
let burnedCardsAnimationTimeOutId;
let endBurnedCardsAnimationTimeOutId;

class GameService {
  onGetInitialStateResult({
    rate,
    cards,
    limits,
    balance,
    userBets,
    roundInfo,
    precision,
    lastResults,
    currentBets,
    userFreebets,
    currencyCode,
    gameTypeInfo,
    // isTestPlatform,
    burnedCardsInfo,
    gameConfiguration,
  }) {
    applicationStore.setUseDemoByBackendLogic(false);
    // platformMessageStore.setIsUsedGetBalanceBySocketInvoke(false);
    this.handleResetDataInitial();
    applicationStore.setTranslator((arg) => arg);

    const alreadyOpenedCards = cards
      .filter(
        ({ card, roundId }) =>
          roundInfo &&
          card !== CARDS.CUTTING_CARD &&
          roundId !== roundInfo?.roundId &&
          roundId !== 0
      )
      .reverse();
    const nextCards = cards
      .filter(({ roundId }) => !roundId || (roundInfo && roundId === roundInfo?.roundId))
      .reverse();
    const { limitsAndPayouts } = gameConfiguration;
    freeBetStore.setIsFreeChip(true);
    cardsStore.setOpenedCardsCount(alreadyOpenedCards.length);
    limitsAndPayoutsStore.setLimitsAndPayouts(limitsAndPayouts);
    roundInfoStore.setRoundInfo({ ...gameTypeInfo, roundId: roundInfo ? roundInfo.roundId : null });
    roundsWinBetTypes.setWinBetTypes(lastResults || []);
    gameResultStore.setGameResults(nextCards);
    gameResultStore.setAdditionalResults(alreadyOpenedCards);
    gameConfigStore.setLimits(limits);
    doBetStore.setQuickBets(limits.quickBets);
    if (!freeBetStore.activeFreeBet.id) {
      doBetStore.setSelectedChip(limits.quickBets[0]);
    }
    userInfoStore.setUserInfo({
      rate,
      balance,
      precision,
      currencyCode,
      demoBalance: 500000,
    });
    userCountStore.setUsersCount(currentBets.length);
    loadingStore.updateLoading({ isGameInitialLoaded: true });
    if (roundInfo?.marketsStatisticsInfo) {
      doBetStore.setLiveBets(roundInfo.marketsStatisticsInfo);
    }
    if (roundInfo && RoundStatus.Started === roundInfo.status && roundInfo.second > 0) {
      const time = Math.floor(Math.abs(roundInfo.second));
      roundTimeStore.setTime(time);
      this.sendWorker(time);
    }
    if (roundInfo && roundInfo.second < 0 && !+gameTypeInfo.shuffledSeconds) {
      if (userBets.length) {
        if (userBets[0].freeBetId) {
          freeBetStore.setActivateFreeBet(
            userFreebets.find((item) => item.id === userBets[0].freeBetId)
          );
        }
        doBetStore.setBetsModel({
          betModel: userBets[0].betData.map((item) => {
            const { winAmount, ...all } = item;
            return all;
          }),
        });
      }
      gameStore.setIsBetsClosed(true);
      if (gameTypeInfo.cuttingCardPlace < 0) {
        cardsStore.setIsCuttingCardOpen(true);
      }
      roundInfo.roundCards.forEach((openedCard) => {
        cardsStore.updateTableCards(openedCard);
      });
    }
    if (roundInfo && roundInfo.status === RoundStatus.OnFinish) {
      if (userBets.length) {
        doBetStore.setBetsModel({
          betModel: userBets[0].betData,
        });
        winningAmountStore.setWinningAmount(userBets[0].winAmount);
        winningAmountStore.setLastWin(userBets[0].winAmount);
        if (roundInfo.roundData) {
          winningAmountStore.setWinType(roundInfo.roundData.winType);
        }
      }
      const winingUsers = currentBets.filter((user) => user.winAmount !== 0);
      if (winingUsers.length) {
        winingUsers.sort((a, b) => b.winAmount - a.winAmount);
        userWinCountStore.setUserWiningBets(winingUsers);
      }
    }
    if (gameTypeInfo.shuffledSeconds) {
      const { shuffledSeconds } = gameTypeInfo;
      gameStore.setIsBetsClosed(true);
      this.handleResetEndedRoundData();
      this.handleShuffleAnimationInitial({ burnedCardsInfo, shuffledSeconds });
    }
    // cashbackStore.getUserPredictions();
    // cashbackStore.getUserPredictionsHistory();
    popupStore.toggleReconnect(false);
    popupStore.onConnectionError(false);
    popupStore.close();
  }

  handleDoBetResult(data) {
    const { userId } = userInfoStore;
    const { amount } = data;
    userCountStore.setUsersCount(
      data.betData.length ? userCountStore.usersCount + 1 : userCountStore.usersCount
    );
    if (data.userId === +userId) {
      doBetStore.setReBet(data.betData.map(({ winAmount, ...all }) => all));
      doBetStore.setReBetAmount(amount);
      if (userSettingsStore.isSoundOn) {
        soundService.playBetAccepted();
      }
      doBetStore.handleNotificationCode(data.responseCode);
    }
  }

  handleDoBetResultValidation(data) {
    doBetStore.resetBetsModels();
    doBetStore.handleNotificationCode(data.responseCode);
  }

  handleRoundStart({ data: { startedRound } }) {
    cardsStore.setBurnedCards([]);
    cardsStore.setBurnedCardsCount(0);
    userCountStore.setUsersCount(0);
    roundInfoStore.setRoundId(startedRound.roundId);
    this.handleFilterResultsOpenedCards();
    this.sendWorker(roundInfoStore.duration);
    this.handleResetEndedRoundData();
    gameStore.setIsBetsClosed(false);
  }

  handleResultReport({ data: { resultReportData } }) {
    const { openedCard, message, cardsResult } = resultReportData;
    if (openedCard) {
      soundService.play(soundTypes.Card);
      cardsStore.updateTableCards(openedCard);
    }
    if (message === SHUFFLED_MESSAGE) {
      const lastAndNextResults = cardsResult.reverse().reduce(
        (acc, cur) => {
          if (cur.roundId) {
            acc.alreadyOpenedCards.push(cur);
          } else {
            acc.nextCards.push(cur);
          }
          return acc;
        },
        {
          alreadyOpenedCards: [],
          nextCards: [],
        }
      );
      gameResultStore.setGameResults(lastAndNextResults.nextCards);
      gameResultStore.setAdditionalResults(lastAndNextResults.alreadyOpenedCards);
      this.handleResetEndedRoundData();
      this.handleShuffleStarting(resultReportData);
    }
  }

  // async handleGetBalance() {
  //   const {
  //     data: { result },
  //   } = await ApiServices.get(ApiServiceTypes.UsersService).getBalance();
  //   if (result) {
  //     userInfoStore.setBalance(result.balance);
  //   }
  // }

  // handleGetBalanceResult(balance) {
  //   userInfoStore.setBalance(balance);
  // }

  handleRoundFin({ data: { finishedRound, bets } }) {
    const { roundData, id } = finishedRound;
    const { userId } = userInfoStore;
    const { isDemo } = applicationStore;
    const { freeBetTotalWinCalculator } = freeBetStore;
    const currentUserBet = bets.find((bet) => +bet.userId === +userId);

    winningAmountStore.setWinType(roundData.winType);

    if (isDemo && doBetStore.lastBetModel?.betModel) {
      this.handleDemoResult(roundData);
    }

    if (currentUserBet) {
      doBetStore.setBetsModel({
        betModel: currentUserBet.additionalParams,
      });
      // this.handleGetBalance();
      gameLiveConnection.getBalance();
      if (currentUserBet.freeBetId) {
        freeBetTotalWinCalculator(currentUserBet);
      }
      winningAmountStore.setWinningAmount(currentUserBet.winAmount);
      winningAmountStore.setLastWin(currentUserBet.winAmount);
      setTimeout(() => {
        tableBetHistoryStore.handleBetHistoryUpdate();
      }, 2000);
    }
    setTimeout(() => {
      roundsWinBetTypes.updateWinBetTypes({ roundId: id, roundData });
    }, 2000);

    const winingUsers = bets.filter((user) => user.winAmount !== 0);
    if (winingUsers.length) {
      winingUsers.sort((a, b) => b.winAmount - a.winAmount);
      userWinCountStore.setUserWiningBets(winingUsers);
    }
  }

  handleBetsClosed({ data: { gameTypeId, roundId } }) {
    gameStore.setIsBetsClosed(true);
    const { setDemoBalance, balance } = userInfoStore;
    const { activeFreeBet } = freeBetStore;

    // gameStore.setIsNotification(true);
    // gameStore.setNotificationText(NOTIFICATION_TEXT.NO_MORE_BETS);
    // setTimeout(() => {
    //   gameStore.setNotificationText(NOTIFICATION_TEXT.DEALING_TIME);
    //   setTimeout(() => {
    //     gameStore.setNotificationText('');
    //     gameStore.setIsNotification(false);
    //   }, 2000);
    // }, 2000);
    if (applicationStore.isDemo) {
      if (doBetStore.lastBetModel?.betModel) {
        doBetStore.handleNotificationCode(betResponseCodeByName.Success);
        setDemoBalance(
          parseNumberByPrecision(1, balance - doBetStore.betsTotalAmount, +userInfoStore.precision)
        );
        doBetStore.setReBet(doBetStore.lastBetModel?.betModel);
        doBetStore.setReBetAmount(doBetStore.betsTotalAmount);
      }
    } else {
      const { betsModels } = doBetStore;
      const betData = betsModels[betsModels.length - 1]?.betModel;
      if (betData) {
        gameLiveConnection.doBet(
          activeFreeBet.id
            ? {
                betData,
                gameTypeId,
                roundId,
                freeBetId: activeFreeBet.id,
              }
            : {
                betData,
                gameTypeId,
                roundId,
              }
        );
      }
    }
  }

  handleDoChipResult(data) {
    if (data) {
      doBetStore.setLiveBets(data);
    }
  }

  socketRecursiveReconnecting() {
    try {
      liveConnection
        .restartGameConnection()
        .then((connectionData) => {
          liveConnection.connectionInitCallback(connectionData);
          clearTimeout(timeOutId);
        })
        .catch(() => {
          timeOutId = setTimeout(() => {
            this.socketRecursiveReconnecting();
          }, 4000);
        });
    } catch (err) {
      popupStore.open({ name: PopUps.ConnectionError });
      soundService.muteAll();
      popupStore.onConnectionError(true);
      popupStore.toggleReconnect(true);
    }
  }

  handleReconnecting(error) {
    if (error && error.message) {
      soundService.muteAll();
      // eslint-disable-next-line
      popupStore.open({ name: PopUps.Reconnect });
      popupStore.toggleReconnect(true);
    } else {
      GameService.ReconnectCount += 1;
      popupStore.open({ name: PopUps.Reconnect });
      popupStore.toggleReconnect(true);
    }
  }

  handleReconnected() {
    GameService.ReconnectCount = 0;
    if (popupStore.popupName !== PopUps.ConnectionError) {
      gameLiveConnection.sendGameGetInitialState(false).then(() => {});
    }
  }

  handleConnectionError() {
    soundService.muteAll();
    if (GameService.ReconnectCount) {
      popupStore.open({ name: PopUps.ConnectionError });
      popupStore.onConnectionError(true);
    }
  }

  handleGameOpen() {
    gameLiveConnection.stop();
  }

  countDown({ data: time }) {
    roundTimeStore.setTime(time);
  }

  sendWorker(timerValue) {
    if (timerValue) {
      worker.addEventListener('message', this.countDown);
      worker.postMessage(timerValue);
    } else {
      worker.removeEventListener('message', this.countDown);
    }
  }

  handleDemoResult({ winType }) {
    const { setDemoBalance, balance } = userInfoStore;
    const demoWinAmount = doBetStore.lastBetModel.betModel.reduce((acc, cur) => {
      if (winType === cur.type && winType !== BET_TYPES.draw) {
        acc += parseNumberByPrecision(
          1,
          cur.amount * BET_TYPES_ODDS[winType],
          +userInfoStore.precision
        );
      }
      if (winType === cur.type && winType === BET_TYPES.draw) {
        acc += parseNumberByPrecision(
          1,
          cur.type === BET_TYPES.draw ? cur.amount * BET_TYPES_ODDS[winType] : cur.amount / 2,
          +userInfoStore.precision
        );
      }
      return parseNumberByPrecision(1, acc, +userInfoStore.precision);
    }, 0);

    const finishedBetModel = doBetStore.lastBetModel.betModel.map((item) => {
      if (winType !== BET_TYPES.draw) {
        return {
          ...item,
          winAmount: parseNumberByPrecision(
            1,
            item.type === winType ? item.amount * BET_TYPES_ODDS[winType] : 0,
            +userInfoStore.precision
          ),
        };
      }
      return {
        ...item,
        winAmount: parseNumberByPrecision(
          1,
          item.type === BET_TYPES.draw ? item.amount * BET_TYPES_ODDS[winType] : item.amount / 2,
          +userInfoStore.precision
        ),
      };
    });
    doBetStore.setBetsModel({
      betModel: finishedBetModel,
    });
    setDemoBalance(balance + demoWinAmount);
    winningAmountStore.setWinningAmount(demoWinAmount);
    winningAmountStore.setLastWin(demoWinAmount);
  }

  handleFilterResultsOpenedCards() {
    const alreadyOpenedCards = gameResultStore.gameResults.filter(({ roundId }) => roundId !== 0);
    gameResultStore.setAdditionalResults([
      ...alreadyOpenedCards,
      ...gameResultStore.additionalResults,
    ]);
    const results = [...gameResultStore.gameResults];
    results.length -= alreadyOpenedCards.length;
    gameResultStore.setGameResults(results);
  }

  handleResetDataInitial() {
    if (animationsTimeOutId.length) {
      animationsTimeOutId.forEach((id) => {
        clearTimeout(id);
      });
      animationsTimeOutId.length = 0;
    }
    cardsStore.setBurnedCardsCount(0);
    winningAmountStore.setLastWin(0);
    doBetStore.setLiveBets();
    doBetStore.resetBetsModels();
    doBetStore.setReBet(null);
    doBetStore.setReBetAmount(0);
    cardsStore.resetCardsInfo();
    winningAmountStore.setWinType(null);
    winningAmountStore.setWinningAmount(null);
    roundsWinBetTypes.setWinBetTypes([]);
    cardsStore.setBurnedCards([]);
    cardsStore.setIsShuffle(false);
    cardsStore.setShuffleAnimationType('');
    cardsStore.setOpenedCardsCount(0);
    gameResultStore.setAdditionalResults([]);
    cardsStore.setIsCuttingCardOpen(false);
    gameStore.setIsBetsClosed(false);
  }

  handleResetEndedRoundData() {
    doBetStore.setLiveBets();
    cardsStore.resetCardsInfo();
    doBetStore.resetBetsModels();
    winningAmountStore.setWinType(null);
    winningAmountStore.setWinningAmount(null);
    cardsStore.setIsCuttingCardOpen(false);
    userWinCountStore.setUserWiningBets(null);
  }

  handleShuffleStarting({ newShuffledCards, burnedCardsInfo }) {
    const animData = isMobile() ? MobileShuffleAnimation : WebShuffleAnimation;
    cardsStore.setIsShuffle(true);
    // shuffle animation start
    let animationDuration = 0;
    for (let i = 0; i < animData.length; i += 1) {
      shuffleAnimationTimeOutId = setTimeout(() => {
        cardsStore.setShuffleAnimationType(animData[i].payload.current.join(' '));
      }, animationDuration);
      animationsTimeOutId.push(shuffleAnimationTimeOutId);
      animationDuration += animData[i].payload.duration;
    }

    endShuffleAnimationTimeOutId = setTimeout(() => {
      gameResultStore.setGameResults(newShuffledCards.reverse());
      gameResultStore.setAdditionalResults([]);
      roundsWinBetTypes.setWinBetTypes([]);
      cardsStore.setOpenedCardsCount(0);
    }, animationDuration);
    animationsTimeOutId.push(endShuffleAnimationTimeOutId);
    animationDuration += 1000;
    // shuffle animation end

    // burned cards animation start
    cardsStore.setBurnedCardsCount(burnedCardsInfo.burnedCardsCount);
    for (let i = 0; i < burnedCardsInfo.burnedCardsCount + 1; i += 1) {
      animationDuration += 500;
      burnedCardsAnimationTimeOutId = setTimeout(() => {
        soundService.play(soundTypes.Card);
        cardsStore.setShuffleAnimationType('');
        cardsStore.setIsShuffle(false);
        cardsStore.updateBurnedCards(i === 0 ? burnedCardsInfo.firstBurnedCard : null);
      }, animationDuration);
      animationsTimeOutId.push(burnedCardsAnimationTimeOutId);
    }
    animationDuration += 1000;

    endBurnedCardsAnimationTimeOutId = setTimeout(() => {
      cardsStore.setBurnedCards([]);
      cardsStore.setBurnedCardsCount(0);
    }, animationDuration);
    animationsTimeOutId.push(endBurnedCardsAnimationTimeOutId);
    // burned cards animation start
  }

  handleShuffleAnimationInitial({ burnedCardsInfo, shuffledSeconds }) {
    const animData = isMobile() ? MobileShuffleAnimation : WebShuffleAnimation;
    cardsStore.setIsShuffle(true);
    // shuffle animation start
    let animationDuration = 0;
    let skipAnimationSecond = 0;
    for (let i = 0; i < animData.length; i += 1) {
      skipAnimationSecond += animData[i].payload.duration;
      if (shuffledSeconds * 1000 + 700 < skipAnimationSecond) {
        shuffleAnimationTimeOutId = setTimeout(() => {
          cardsStore.setShuffleAnimationType(animData[i].payload.current.join(' '));
        }, animationDuration);
        animationsTimeOutId.push(shuffleAnimationTimeOutId);
        const skipTime = animData[i].payload.duration - (skipAnimationSecond - shuffledSeconds);
        animationDuration += skipTime < 0 ? animData[i].payload.duration : skipTime;
      }
    }

    if (shuffledSeconds * 1000 < skipAnimationSecond) {
      animationDuration += 1000;
      endShuffleAnimationTimeOutId = setTimeout(() => {
        gameResultStore.setAdditionalResults([]);
        roundsWinBetTypes.setWinBetTypes([]);
        cardsStore.setOpenedCardsCount(0);
      }, animationDuration);
      animationsTimeOutId.push(shuffleAnimationTimeOutId);
      skipAnimationSecond += 1000;
    }
    // shuffle animation end

    // burned cards animation start
    cardsStore.setBurnedCardsCount(burnedCardsInfo.burnedCardsCount);
    if (shuffledSeconds * 1000 < skipAnimationSecond) {
      animationDuration += 700;
      for (let i = 0; i < burnedCardsInfo.burnedCardsCount + 1; i += 1) {
        animationDuration += 300;
        burnedCardsAnimationTimeOutId = setTimeout(() => {
          soundService.play(soundTypes.Card);
          cardsStore.setShuffleAnimationType('');
          cardsStore.setIsShuffle(false);
          cardsStore.updateBurnedCards(i === 0 ? burnedCardsInfo.firstBurnedCard : null);
        }, animationDuration);
        animationsTimeOutId.push(burnedCardsAnimationTimeOutId);
      }
      animationDuration += 1000;
      endBurnedCardsAnimationTimeOutId = setTimeout(() => {
        cardsStore.setBurnedCards([]);
        cardsStore.setBurnedCardsCount(0);
      }, animationDuration);
      animationsTimeOutId.push(endBurnedCardsAnimationTimeOutId);
    } else {
      cardsStore.setBurnedCardsCount(burnedCardsInfo.burnedCardsCount);
      for (let i = 0; i < burnedCardsInfo.burnedCardsCount + 1; i += 1) {
        cardsStore.setShuffleAnimationType('');
        cardsStore.setIsShuffle(false);
        cardsStore.updateBurnedCards(i === 0 ? burnedCardsInfo.firstBurnedCard : null);
      }
    }
  }
}

export const gameService = new GameService();
